import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { UseMutationOptions, useQueryClient } from 'react-query';
import http from '../config/http-service';
import { restaurantInquiriesEndpoint } from './use-create-restaurant-inquiry-api';

// DELETE api/v1/cms/restaurant-inquiries/:id/items/:itemId — soft-deletes one
// line item (header and other items untouched). Response is just the item's id
// echoed back; the invalidation below refetches detail + list (itemsCount
// filters deleted rows server-side). 404 if the item isn't under this inquiry.
type TData = { id: number };
type TError = AxiosError;
type TVariables = { id: number | string; itemId: number };

type Options = Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>;
type TReturnType = ReturnType<typeof useMutationWrapper<TData, TError, TVariables>>;

export function useDeleteRestaurantInquiryItemApi(options?: Options): TReturnType {
  const queryClient = useQueryClient();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async ({ id, itemId }: TVariables): Promise<TData> => {
    const response = await http.delete(`${restaurantInquiriesEndpoint}/${id}/items/${itemId}`);
    return response.data?.data ?? response.data;
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [restaurantInquiriesEndpoint, 'delete-item'],
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([restaurantInquiriesEndpoint]);
      userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}
